import React from 'react';
import {Button, FlexRow, SplitButton, Tooltip} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {useReportExport} from './useReportExport';
import {useBuilderShareDialog} from './useBuilderShareDialog';

/** Top bar shown in view mode. Editors additionally see "Share" and "Edit Report" (opens the edit
 * session via `beginEdit()`, which switches `mode` to `'edit'` once the draft is opened); readers
 * only ever see "Download". Download exports the report. Share opens the report-sharing dialog once
 * the report is saved; on an unsaved draft it stays disabled with a tooltip explaining why, since a
 * draft has no Reports-list entry to share. Left-to-right order is Download, Share, Edit Report,
 * matching the design — the reverse of DOM/visual emphasis order used previously. Download uses the
 * same `SplitButton`-with-caret pattern as EditTopBar — every screenshot shows the dropdown caret in
 * both modes. */
export function ViewTopBar(): JSX.Element {
	const {formatMessage} = useIntl();
	const {role, beginEdit, savedReportId, updateSavedReportPrivateAccess} = useReportBuilder();
	const download = useReportExport();
	const {open: openShare, loading: shareLoading} = useBuilderShareDialog(updateSavedReportPrivateAccess);

	// No stub tooltip here any more: Download exports for real, so the only disabled affordance left is
	// Share on an unsaved draft, which says why.
	const unsavedShareTooltip = formatMessage({id: 'ai_report_builder.share_requires_save'});

	return (
		<FlexRow justifyContent={'flex-end'} alignItems={'center'} gap={'s'} data-cy={'ai-report-view-top-bar'}>
			{/* SplitButton.Section only renders its trailing dropdown caret when `dropdownMenuOptions`
			 * is non-empty (see SplitButton.js), so the caret section carries the export formats. CSV is
			 * the only one the report service produces today, and keeping it a menu means a second
			 * format is one more entry rather than a new control. Pressing either half exports — the
			 * primary half runs the one format directly rather than being decorative.
			 * While an export runs the primary half shows the design system's `waiting` animation and
			 * the caret is closed off with it, so the pair reads as working and a second export cannot
			 * be started from either.
			 * The waiting state is visual only: BaseButton hard-codes the accessible name to
			 * "animation-waiting" while `waiting` is set, so it cannot be told what it is busy doing
			 * from here. That is a design-system fix; until it lands, the busy state is announced by
			 * the live region below instead, which does not depend on the button's own name. */}
			<SplitButton>
				<SplitButton.Section
					emphasis={'medium'}
					icon={'download'}
					waiting={download.exporting}
					aria-busy={download.exporting}
					onPress={download.start}
					text={formatMessage({id: 'ai_report_builder.download'})}
				/>
				<SplitButton.Section
					emphasis={'medium'}
					icon={'chevronDown'}
					disabled={download.exporting}
					iconOnly
					// The design system derives a button's accessible name from its text children and
					// falls back to the icon name for an icon-only one, overriding this label; it is
					// kept as the statement of what this half is for, for whenever that is fixed.
					aria-label={formatMessage({id: 'ai_report_builder.download_options'})}
					dropdownMenuOptions={[
						{
							name: formatMessage({id: 'ai_report_builder.download_csv'}),
							onClick: download.start,
						},
					]}
				/>
			</SplitButton>
			{download.statusRegion}
			{role === 'editor' ? (
				savedReportId === null ? (
					<Tooltip content={unsavedShareTooltip}>
						<span>
							<Button
								emphasis={'medium'}
								icon={'share'}
								disabled
								data-cy={'ai-report-share-button'}
								aria-label={formatMessage({id: 'ai_report_builder.share'})}
							>
								{formatMessage({id: 'ai_report_builder.share'})}
							</Button>
						</span>
					</Tooltip>
				) : (
					<Button
						emphasis={'medium'}
						icon={'share'}
						disabled={shareLoading}
						onPress={() => openShare(savedReportId)}
						data-cy={'ai-report-share-button'}
						aria-label={formatMessage({id: 'ai_report_builder.share'})}
					>
						{formatMessage({id: 'ai_report_builder.share'})}
					</Button>
				)
			) : null}
			{role === 'editor' ? (
				<Button
					emphasis={'high'}
					icon={'edit'}
					onPress={() => void beginEdit()}
					data-cy={'ai-report-edit-report-button'}
				>
					{formatMessage({id: 'ai_report_builder.edit_report'})}
				</Button>
			) : null}
		</FlexRow>
	);
}
